import { NavLink } from 'react-router-dom'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { ROUTES } from '../../../constants/routes.constants'
import { StorefrontBackdrop } from '../storefront/chrome'

/**
 * CustomerSidebar — left slide-in drawer for the signed-in customer.
 * Mirrors OperatorSidebar's chrome + motion so the customer side feels
 * like the same app, just with the customer's self-service surfaces
 * (Orders History, Returns, Testimony). Kiran 2026-06-17.
 *
 * Visibility is fixed (no Layer-2c gating) — every customer sees the
 * same 3 entries. The hamburger that toggles it lives in StorefrontNav
 * (`onMenu` callback); the page renders the drawer + the dim.
 */
const LINKS: ReadonlyArray<{ to: string; label: string }> = [
  { to: ROUTES.ACCOUNTORDERS, label: 'Orders History' },
  { to: ROUTES.ACCOUNTRETURNS, label: 'Returns' },
  { to: ROUTES.ACCOUNTTESTIMONY, label: 'Testimony' }
]

export function CustomerSidebar({ drawerOpen }: { readonly drawerOpen: boolean }): JSX.Element {
  const reduce = useReducedMotion() === true

  const listV: Variants = {
    show: { transition: { staggerChildren: reduce ? 0 : 0.05, delayChildren: reduce ? 0 : 0.08 } }
  }
  const itemV: Variants = {
    hidden: { opacity: reduce ? 1 : 0, x: reduce ? 0 : -10 },
    show: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } }
  }

  const itemClass = ({ isActive }: { isActive: boolean }): string =>
    `relative flex items-center justify-between gap-2 rounded-md px-3 py-2.5 transition-all duration-200 ${
      isActive
        ? 'text-text-light font-semibold'
        : 'text-text-light hover:translate-x-1 hover:bg-text-light/10'
    }`

  const activePill = (isActive: boolean): JSX.Element | null =>
    isActive ? (
      <motion.span
        layoutId="customerActive"
        className="absolute inset-0 z-0 rounded-md border-l-2 border-forest bg-text-light/15"
        transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 30 }}
      />
    ) : null

  return (
    <aside
      aria-hidden={!drawerOpen}
      className={`fixed bottom-0 left-0 top-20 z-40 w-72 overflow-hidden shadow-2xl transition-transform duration-300 ease-out ${
        drawerOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <StorefrontBackdrop />
      <div className="absolute inset-3 z-10 overflow-hidden rounded-2xl bg-gradient-to-b from-bg-dark to-charcoal text-text-light shadow-lg">
        <motion.nav
          className="h-full overflow-y-auto px-3 py-5"
          initial="hidden"
          animate={drawerOpen ? 'show' : 'hidden'}
          variants={listV}
        >
          <p className="mb-3 px-3 font-heading text-micro uppercase tracking-wider text-text-light/70">
            Your account
          </p>
          <ul className="space-y-1 font-body text-body-sm">
            {LINKS.map((l) => (
              <motion.li key={l.to} variants={itemV}>
                <NavLink to={l.to} end className={itemClass}>
                  {({ isActive }) => (
                    <>
                      {activePill(isActive)}
                      <span className="relative z-10">{l.label}</span>
                    </>
                  )}
                </NavLink>
              </motion.li>
            ))}
          </ul>
        </motion.nav>
      </div>
    </aside>
  )
}
